/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import { useEffect, useState, useRef } from 'react';
import {
    Button,
    TextField,
    Select,
    MenuItem,
    Typography,
    Tooltip,
    IconButton,
    CircularProgress,
    LinearProgress,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ReactComponent as Stepper1 } from '../../../../../../../assets/images/AI-stepper1.svg';
import { ReactComponent as Stepper2 } from '../../../../../../../assets/images/AI-stepper2.svg';
import * as S from './SOPWriter.styled';
import api from 'api';
import ReactQuill from 'react-quill';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import { loadDocs } from 'redux/application/documents/documents-middleware';
import AlertMessage, { AlertObj } from 'components/common/alert';
import {
    Actions,
    Modal,
    ModalHeader,
    PrimaryButton,
    SecondaryButton,
} from 'components/common/Modal';

const SOPTypes = {
    studentVisa: 'studentVisa',
    visitorVisa: 'visitorVisa',
    AFFIDAVIT_OF_FINANCIAL_SUPPORT_FOR_TOURIST_VISA:
        'AFFIDAVIT_OF_FINANCIAL_SUPPORT_FOR_TOURIST_VISA',
    TOURIST_VISA_INVITATION_LETTER: 'TOURIST_VISA_INVITATION_LETTER',
    SUBMISSION_LETTER_TOURIST_VISA: 'SUBMISSION_LETTER_TOURIST_VISA',
    SUBMISSION_LETTER_STUDENT_VISA: 'SUBMISSION_LETTER_STUDENT_VISA',
    STUDENT_VISA_AFFIDAVIT_FINANCIAL_SUPPORT: 'STUDENT_VISA_AFFIDAVIT_FINANCIAL_SUPPORT',
    REFERENCE_LETTER_FOR_STUDENT_VISA: 'REFERENCE_LETTER_FOR_STUDENT_VISA',
    EMPLOYMENT_VERIFICATION_LETTER_FOR_VISA: 'EMPLOYMENT_VERIFICATION_LETTER_FOR_VISA',
    TV_AFFIDAVIT_FINANCIAL_PURPOSE: 'TV_AFFIDAVIT_FINANCIAL_PURPOSE',
};

const sopTypesOptions = [
    { value: SOPTypes.studentVisa, label: 'Student Visa' },
    { value: SOPTypes.visitorVisa, label: 'Visitor Visa' },
    {
        value: SOPTypes.AFFIDAVIT_OF_FINANCIAL_SUPPORT_FOR_TOURIST_VISA,
        label: 'Tourist Visa: Financial Support Affidavit',
    },
    { value: SOPTypes.TOURIST_VISA_INVITATION_LETTER, label: 'Tourist Visa: Invitation Letter' },
    { value: SOPTypes.SUBMISSION_LETTER_TOURIST_VISA, label: 'Submission Letter - Tourist Visa' },
    { value: SOPTypes.SUBMISSION_LETTER_STUDENT_VISA, label: 'Submission Letter - Student Visa' },
    {
        value: SOPTypes.STUDENT_VISA_AFFIDAVIT_FINANCIAL_SUPPORT,
        label: 'Student Visa: Affidavit of Financial Support',
    },
    {
        value: SOPTypes.REFERENCE_LETTER_FOR_STUDENT_VISA,
        label: 'Reference Letter for Student Visa',
    },
    {
        value: SOPTypes.EMPLOYMENT_VERIFICATION_LETTER_FOR_VISA,
        label: 'Employment Verification Letter for Visa',
    },
    {
        value: SOPTypes.TV_AFFIDAVIT_FINANCIAL_PURPOSE,
        label: 'Tourist Visa Affidavit of Financial Support/Purpose',
    },
];

const defaultFormState = {
    sopType: SOPTypes.studentVisa,
    institutionName: '',
    studyProgram: '',
    careerGoals: '',
    futurePlans: '',
    familyTies: '',
    homeCountryTies: '',
    financialSupport: '',
    additionalDetails: '',
    purpose: '',
    sponsorName: '',
    sponsorCitizenship: '',
    relationship: '',
    sponsorAddress: '',
    sponsorOccupation: '',
    applicantPassport: '',
    travelStart: '',
    travelEnd: '',
    locations: '',
};

export default function MultiStepModal({ open, onClose }: { open: boolean; onClose: () => void }) {
    const dispatch = useDispatch<AppDispatch>();
    const [isShowingAlert, setShowingAlert] = useState(false);

    const [alertMessage, setAlertMessage] = useState<AlertObj>({ type: 'info', value: '' });
    const quillRef = useRef<ReactQuill | null>(null);
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState<Record<string, any>>({ ...defaultFormState });

    type SOPType = (typeof SOPTypes)[keyof typeof SOPTypes];

    const requiredFields: Record<SOPType, string[]> = {
        [SOPTypes.studentVisa]: [
            'sopType',
            'institutionName',
            'studyProgram',
            'careerGoals',
            'futurePlans',
            'familyTies',
            'homeCountryTies',
            'financialSupport',
        ],
        [SOPTypes.visitorVisa]: [
            'sopType',
            'purpose',
            'futurePlans',
            'familyTies',
            'homeCountryTies',
            'financialSupport',
        ],
        [SOPTypes.AFFIDAVIT_OF_FINANCIAL_SUPPORT_FOR_TOURIST_VISA]: [
            'sopType',
            'sponsorName',
            'sponsorCitizenship',
            'relationship',
            'sponsorAddress',
            'sponsorOccupation',
            'applicantPassport',
            'purpose',
            'travelStart',
            'travelEnd',
            'locations',
        ],
        [SOPTypes.EMPLOYMENT_VERIFICATION_LETTER_FOR_VISA]: [
            'sopType',
            'passportNumber',
            'jobTitle',
            'companyName',
            'companyAddress',
            'employmentStartDate',
            'employmentStatus',
            'salary',
            'responsibilities',
            'leaveStart',
            'leaveEnd',
            'returnConfirmation',
            'signatoryName',
            'signatoryPosition',
            'contactEmail',
            'contactPhone',
        ],
        [SOPTypes.REFERENCE_LETTER_FOR_STUDENT_VISA]: [
            'sopType',
            'studyProgram',
            'institutionName',
            'institutionCountry',
            'refereeName',
            'refereeOccupation',
            'refereeOrganization',
            'refereeAddress',
            'relationship',
            'yearsKnown',
            'refereeContact',
            'homeCountryTies',
            'returnIntent',
        ],
        [SOPTypes.STUDENT_VISA_AFFIDAVIT_FINANCIAL_SUPPORT]: [
            'sopType',
            'studentPassport',
            'program',
            'institutionName',
            'institutionCountry',
            'sponsorName',
            'relationship',
            'sponsorOccupation',
            'sponsorEmployer',
            'sponsorAddress',
            'sponsorContact',
            'sponsorEmail',
            'financialCommitment',
            'sourceOfFunds',
            'returnIntent',
        ],
        [SOPTypes.SUBMISSION_LETTER_STUDENT_VISA]: [
            'sopType',
            'dob',
            'passport',
            'program',
            'institutionName',
            'startDate',
            'duration',
            'tuitionPaid',
            'availableFunds',
            'sourceOfFunds',
            'previousRefusal',
            'refusalExplanation',
            'homeCountryTies',
            'assets',
            'travelHistory',
            'documentsIncluded',
            'representativeName',
            'representativeRCIC',
        ],
        [SOPTypes.SUBMISSION_LETTER_TOURIST_VISA]: [
            'sopType',
            'dob',
            'passport',
            'additionalApplicants',
            'travelStart',
            'travelEnd',
            'purpose',
            'accommodation',
            'employment',
            'residenceCountry',
            'financialSummary',
            'homeCountryTies',
            'documentsIncluded',
            'representativeName',
            'representativeRCIC',
            'visaOffice',
        ],
        [SOPTypes.TOURIST_VISA_INVITATION_LETTER]: [
            'sopType',
            'statusInCanada',
            'inviterOccupation',
            'inviterEmployer',
            'inviterAddress',
            'inviterPhone',
            'inviterEmail',
            'visitorName',
            'visitorPassport',
            'visitorAddress',
            'relationship',
            'visitStart',
            'visitEnd',
            'purpose',
            'itinerary',
            'accommodationDetails',
            'financialResponsibility',
            'supportingDocuments',
            'returnAssurance',
        ],
        [SOPTypes.TV_AFFIDAVIT_FINANCIAL_PURPOSE]: [
            'sopType',
            'passport',
            'sponsorName',
            'relationship',
            'sponsorAddress',
            'sponsorOccupation',
            'incomeSource',
            'purpose',
            'eventDetails',
            'travelStart',
            'travelEnd',
            'financialSupport',
            'funds',
            'bankProof',
            'relationshipEvidence',
            'documentsIncluded',
        ],
    };

    const isStep1Valid = requiredFields[formData.sopType]?.every(
        (field) => formData[field as keyof typeof formData]?.trim() !== ''
    );

    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        setErrors({
            ...errors,
            [name]: name !== 'additionalDetails' && value.trim() === '',
        });
    };

    const [generatedSOP, setGeneratedSOP] = useState('');
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingStep, setLoadingStep] = useState<0 | 1 | 2 | 3>(0);
    const [sopError, setSOPError] = useState<string | null>(null);
    const [retryCount, setRetryCount] = useState(0);

    const LOADING_STEPS = [
        { label: 'Reading applicant profile & education history' },
        { label: 'Building personalized context for the AI model' },
        { label: 'AI is writing your Statement of Purpose...' },
    ];

    useEffect(() => {
        if (step !== 2) return;
        // retryCount is included so re-trying re-fires this effect

        setLoadingStep(0);
        setSOPError(null);
        setLoading(true);

        const stepTimers: ReturnType<typeof setTimeout>[] = [];
        stepTimers.push(setTimeout(() => setLoadingStep(1), 800));
        stepTimers.push(setTimeout(() => setLoadingStep(2), 2000));
        stepTimers.push(setTimeout(() => setLoadingStep(3), 4000));

        const fetchSOP = async () => {
            try {
                setLoading(true);
                setErrors({});
                const generatedText = await getSOP();
                if (!generatedText) {
                    setSOPError('No response received from the AI. Please try again.');
                    return;
                }
                setGeneratedSOP(generatedText);
            } catch (error: any) {
                console.error('Error fetching SOP:', error);
                const serverMessage = error?.response?.data?.error;
                setSOPError(
                    serverMessage ||
                        'The AI model encountered an error. Please try again in a moment.'
                );
            } finally {
                stepTimers.forEach(clearTimeout);
                setLoading(false);
            }
        };

        fetchSOP();

        return () => stepTimers.forEach(clearTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, retryCount]);

    const getSOP = async () => {
        if (activeApp) {
            const resp = await api.sop.getSOP(
                // {
                //     studyProgram: formData.studyProgram,
                //     careerGoals: formData.careerGoals,
                //     futurePlans: formData.futurePlans,
                //     institutionName: formData.institutionName,
                //     familyTies: formData.familyTies,
                //     homeCountryTies: formData.homeCountryTies,
                //     financialSupport: formData.financialSupport,
                //     additionalDetails: formData.additionalDetails,
                // },
                formData,
                activeApp.id,
                formData.sopType
            );
            const rawSop = resp?.data.sop;

            const formattedHtml = rawSop
                .split('\n\n') // Split into paragraphs
                .map((para: string) => {
                    if (!para.trim()) return '<p><br></p>'; // Handle empty lines correctly
                    // Convert single internal \n to <br/>
                    const content = para.replace(/\n/g, '<br/>');
                    return `<p>${content}</p><br/>`;
                })
                .join('');

            return formattedHtml;
        }
    };

    const generateSOPDoc = async () => {
        if (!activeApp) return;

        try {
            const resp = await api.sop.generateSOPDoc(
                { sop_text: generatedSOP },
                activeApp.id,
                formData.sopType
            );

            const blob = new Blob([resp.data], {
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            });

            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = 'Generated_SOP.docx';
            document.body.appendChild(a);
            a.click();

            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            reloadDocList();
            setShowingAlert(true);
            setAlertMessage({
                type: 'success',
                value: 'Document successfully added to the list',
            });
        } catch (error) {
            console.error('Error generating SOP document:', error);
            reloadDocList();
        }
    };

    const reloadDocList = async () => {
        if (activeApp) {
            dispatch(loadDocs(activeApp.id.toString()));
        }
    };

    const Alert = (
        <AlertMessage
            type={alertMessage?.type}
            text={alertMessage?.value}
            open={isShowingAlert}
            onClose={() => setShowingAlert(false)}
        ></AlertMessage>
    );

    const handleModalClose = () => {
        setFormData({ ...defaultFormState });
        setErrors({});
        setStep(1);
        setGeneratedSOP('');
        setSOPError(null);
        setRetryCount(0);
        onClose();
    };

    return (
        <Modal open={open} onClose={handleModalClose} size="lg" ariaLabel="AI Writer">
            {Alert}
            <ModalHeader
                eyebrow="AI Writer"
                title={step === 1 ? 'Generate document' : 'Review AI draft'}
                subtitle={
                    step === 1
                        ? 'Add the case details the AI writer should use for this document.'
                        : 'Review, edit, and download the generated document.'
                }
            />
            <S.StepperWrapper>{step === 1 ? <Stepper1 /> : <Stepper2 />}</S.StepperWrapper>
            <S.ModalContent>
                {step === 1 && formData.sopType === SOPTypes.visitorVisa && (
                    <>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '8px 16px',
                                marginTop: '16px',
                            }}
                        >
                            <SelectField
                                name="sopType"
                                label={<RequiredLabel text="Select SOP Type" />}
                                value={formData.sopType}
                                onChange={handleChange}
                                error={errors.sopType}
                                tooltipText="Choose the purpose of your SOP. For example, select “Student Visa” if you are applying for a Canadian study permit."
                            />
                            <p>
                                <b>Note:</b> Ensure the visa eligibility questionnaire details are
                                accurate, as they will be used by the SOP writer to draft your
                                letter.
                            </p>

                            <TextFieldWithInfo
                                name="purpose"
                                label={<RequiredLabel text="Purpose of Visit" />}
                                value={formData.purpose}
                                onChange={handleChange}
                                error={errors.purpose}
                                tooltipText="Clearly describe why you are visiting Canada — e.g., tourism, attending a family event, visiting relatives, or short business meetings. Include dates and any invitation letters or planned itineraries if available."
                            />
                            <TextFieldWithInfo
                                name="futurePlans"
                                label={<RequiredLabel text="Future Plans (After Returning)" />}
                                value={formData.futurePlans}
                                onChange={handleChange}
                                error={errors.futurePlans}
                                tooltipText="State what you plan to do after your studies in Canada. Include intentions to return home, work in your field, start a business, or continue education"
                            />
                            <TextFieldWithInfo
                                name="familyTies"
                                label={<RequiredLabel text="Family Tie(s)" />}
                                value={formData.familyTies}
                                onChange={handleChange}
                                error={errors.familyTies}
                                tooltipText="Mention immediate family members in your home country (parents, siblings, spouse, children) to show strong personal connections."
                            />
                            <TextFieldWithInfo
                                name="homeCountryTies"
                                label={<RequiredLabel text="Home Country Tie(s)" />}
                                value={formData.homeCountryTies}
                                onChange={handleChange}
                                error={errors.homeCountryTies}
                                tooltipText="Provide additional proof of your intention to return home — such as property ownership, employment prospects, business ties, or ongoing responsibilities."
                            />
                        </div>
                        <TextFieldWithInfo
                            name="financialSupport"
                            label={<RequiredLabel text="Financial Support (If Any)" />}
                            value={formData.financialSupport}
                            onChange={handleChange}
                            error={errors.financialSupport}
                            tooltipText="List who will financially support your studies (e.g., parents, scholarship, personal savings). Mention documents to support this (bank letters, sponsorship, etc.)."
                        />
                    </>
                )}

                {step === 1 && formData.sopType === SOPTypes.studentVisa && (
                    <>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '8px 16px',
                                marginTop: '16px',
                            }}
                        >
                            <SelectField
                                name="sopType"
                                label={<RequiredLabel text="Select SOP Type" />}
                                value={formData.sopType}
                                onChange={handleChange}
                                error={errors.sopType}
                                tooltipText="Choose the purpose of your SOP. For example, select “Student Visa” if you are applying for a Canadian study permit."
                            />
                            <p>
                                <b>Note:</b> Ensure the visa eligibility questionnaire details are
                                accurate, as they will be used by the SOP writer to draft your
                                letter.
                            </p>
                            <TextFieldWithInfo
                                name="institutionName"
                                label={<RequiredLabel text="Select Institution Name" />}
                                value={formData.institutionName}
                                onChange={handleChange}
                                error={errors.institutionName}
                                tooltipText="Enter the full official name of the Canadian institution where you have been accepted (e.g., University of Toronto, Seneca College)."
                            />
                            <TextFieldWithInfo
                                name="studyProgram"
                                label={<RequiredLabel text="Select Program Name" />}
                                value={formData.studyProgram}
                                onChange={handleChange}
                                error={errors.studyProgram}
                                tooltipText="Provide the name of the academic program you are enrolled in (e.g., MSc in Computer Science, Diploma in Business Management)."
                            />
                            <TextFieldWithInfo
                                name="careerGoals"
                                label={<RequiredLabel text="Career Goals" />}
                                value={formData.careerGoals}
                                onChange={handleChange}
                                error={errors.careerGoal}
                                tooltipText="Explain your professional objectives. Describe the job or career path you intend to pursue after completing your studies, and how the program supports this."
                            />
                            <TextFieldWithInfo
                                name="futurePlans"
                                label={<RequiredLabel text="Future Plans (After Returning)" />}
                                value={formData.futurePlans}
                                onChange={handleChange}
                                error={errors.futurePlans}
                                tooltipText="State what you plan to do after your studies in Canada. Include intentions to return home, work in your field, start a business, or continue education"
                            />
                            <TextFieldWithInfo
                                name="familyTies"
                                label={<RequiredLabel text="Family Tie(s)" />}
                                value={formData.familyTies}
                                onChange={handleChange}
                                error={errors.familyTies}
                                tooltipText="Mention immediate family members in your home country (parents, siblings, spouse, children) to show strong personal connections."
                            />
                            <TextFieldWithInfo
                                name="homeCountryTies"
                                label={<RequiredLabel text="Home Country Tie(s)" />}
                                value={formData.homeCountryTies}
                                onChange={handleChange}
                                error={errors.homeCountryTies}
                                tooltipText="Provide additional proof of your intention to return home — such as property ownership, employment prospects, business ties, or ongoing responsibilities."
                            />
                        </div>
                        <TextFieldWithInfo
                            name="financialSupport"
                            label={<RequiredLabel text="Financial Support (If Any)" />}
                            value={formData.financialSupport}
                            onChange={handleChange}
                            error={errors.financialSupport}
                            tooltipText="List who will financially support your studies (e.g., parents, scholarship, personal savings). Mention documents to support this (bank letters, sponsorship, etc.)."
                        />
                        <TextFieldWithInfo
                            name="additionalDetails"
                            label="Additional Details"
                            value={formData.additionalDetails}
                            onChange={handleChange}
                            tooltipText="Use this space to include any relevant information that doesn’t fit elsewhere — such as prior travel history, achievements, or special circumstances."
                        />
                    </>
                )}

                {step === 1 &&
                    formData.sopType ===
                        SOPTypes.AFFIDAVIT_OF_FINANCIAL_SUPPORT_FOR_TOURIST_VISA && (
                        <>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '8px 16px',
                                    marginTop: '16px',
                                }}
                            >
                                <SelectField
                                    name="sopType"
                                    label={<RequiredLabel text="Select SOP Type" />}
                                    value={formData.sopType}
                                    onChange={handleChange}
                                    error={errors.sopType}
                                    tooltipText="Choose the purpose of your SOP. For example, select “Student Visa” if you are applying for a Canadian study permit."
                                />
                                <p>
                                    <b>Note:</b> Ensure the visa eligibility questionnaire details
                                    are accurate, as they will be used by the SOP writer to draft
                                    your letter.
                                </p>
                                <TextFieldWithInfo
                                    name="sponsorName"
                                    label={<RequiredLabel text="Select Sponsor Name" />}
                                    value={formData.sponsorName}
                                    onChange={handleChange}
                                    error={errors.sponsorName}
                                />
                                <TextFieldWithInfo
                                    name="sponsorCitizenship"
                                    label={<RequiredLabel text="Select Sponsor Citizenship" />}
                                    value={formData.sponsorCitizenship}
                                    onChange={handleChange}
                                    error={errors.sponsorCitizenship}
                                    tooltipText="Enter the citizenship of the sponsor (e.g., Canadian, American, etc.)."
                                />
                                <TextFieldWithInfo
                                    name="relationship"
                                    label={<RequiredLabel text="Select Relationship to Sponsor" />}
                                    value={formData.relationship}
                                    onChange={handleChange}
                                    error={errors.relationship}
                                    tooltipText="Specify your relationship to the sponsor (e.g., parent, spouse, sibling, friend)."
                                />
                                <TextFieldWithInfo
                                    name="sponsorAddress"
                                    label={<RequiredLabel text="Sponsor Address" />}
                                    value={formData.sponsorAddress}
                                    onChange={handleChange}
                                    error={errors.sponsorAddress}
                                    tooltipText="Provide the full address of the sponsor (e.g., street, city, province, postal code)."
                                />
                                <TextFieldWithInfo
                                    name="sponsorOccupation"
                                    label={<RequiredLabel text="Sponsor Occupation" />}
                                    value={formData.sponsorOccupation}
                                    onChange={handleChange}
                                    error={errors.sponsorOccupation}
                                    tooltipText="Provide the occupation of the sponsor (e.g., engineer, teacher, business owner)."
                                />
                                <TextFieldWithInfo
                                    name="applicantPassport"
                                    label={<RequiredLabel text="Applicant Passport Number" />}
                                    value={formData.applicantPassport}
                                    onChange={handleChange}
                                    error={errors.applicantPassport}
                                    tooltipText="Provide the passport number of the applicant."
                                />
                                <TextFieldWithInfo
                                    name="purpose"
                                    label={<RequiredLabel text="Purpose of Visit" />}
                                    value={formData.purpose}
                                    onChange={handleChange}
                                    error={errors.purpose}
                                    tooltipText="Clearly describe why you are visiting Canada — e.g., tourism, attending a family event, visiting relatives, or short business meetings. Include dates and any invitation letters or planned itineraries if available."
                                />
                                <DateFieldWithInfo
                                    name="travelStart"
                                    label={<RequiredLabel text="Travel Start Date" />}
                                    value={formData.travelStart}
                                    onChange={handleChange}
                                    error={errors.travelStart}
                                    tooltipText="Provide the date you plan to start your travel to Canada."
                                />
                                <DateFieldWithInfo
                                    name="travelEnd"
                                    label={<RequiredLabel text="Travel End Date" />}
                                    value={formData.travelEnd}
                                    onChange={handleChange}
                                    error={errors.travelEnd}
                                    tooltipText="Provide the date you plan to end your travel to Canada."
                                />
                                <TextFieldWithInfo
                                    name="locations"
                                    label={<RequiredLabel text="Planned Locations in Canada" />}
                                    value={formData.locations}
                                    onChange={handleChange}
                                    error={errors.locations}
                                    tooltipText="List the cities or provinces you plan to visit in Canada (e.g., Toronto, Vancouver, Montreal)."
                                />
                            </div>
                        </>
                    )}

                {step === 1 &&
                    formData.sopType === SOPTypes.EMPLOYMENT_VERIFICATION_LETTER_FOR_VISA && (
                        <>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '8px 16px',
                                    marginTop: '16px',
                                }}
                            >
                                <SelectField
                                    name="sopType"
                                    label={<RequiredLabel text="Select SOP Type" />}
                                    value={formData.sopType}
                                    onChange={handleChange}
                                    error={errors.sopType}
                                    tooltipText="Choose the purpose of your SOP. For example, select “Student Visa” if you are applying for a Canadian study permit."
                                />
                                <p>
                                    <b>Note:</b> Ensure the visa eligibility questionnaire details
                                    are accurate, as they will be used by the SOP writer to draft
                                    your letter.
                                </p>
                                <TextFieldWithInfo
                                    name="passportNumber"
                                    label={<RequiredLabel text="Passport Number" />}
                                    value={formData.passportNumber}
                                    onChange={handleChange}
                                    error={errors.passportNumber}
                                />
                                <TextFieldWithInfo
                                    name="jobTitle"
                                    label={<RequiredLabel text="Job Title" />}
                                    value={formData.jobTitle}
                                    onChange={handleChange}
                                    error={errors.jobTitle}
                                    tooltipText="Enter the job title of the sponsor (e.g., manager, engineer, teacher)."
                                />
                                <TextFieldWithInfo
                                    name="companyName"
                                    label={<RequiredLabel text="Company Name" />}
                                    value={formData.companyName}
                                    onChange={handleChange}
                                    error={errors.companyName}
                                    tooltipText="Enter the name of the company where the sponsor is employed."
                                />
                                <TextFieldWithInfo
                                    name="companyAddress"
                                    label={<RequiredLabel text="Company Address" />}
                                    value={formData.companyAddress}
                                    onChange={handleChange}
                                    error={errors.companyAddress}
                                    tooltipText="Provide the full address of the company where the sponsor is employed."
                                />
                                <DateFieldWithInfo
                                    name="employmentStartDate"
                                    label={<RequiredLabel text="Employment Start Date" />}
                                    value={formData.employmentStartDate}
                                    onChange={handleChange}
                                    error={errors.employmentStartDate}
                                    tooltipText="Provide the start date of the sponsor's employment (e.g., month and year)."
                                />
                                <TextFieldWithInfo
                                    name="employmentStatus"
                                    label={<RequiredLabel text="Employment Status" />}
                                    value={formData.employmentStatus}
                                    onChange={handleChange}
                                    error={errors.employmentStatus}
                                />
                                <TextFieldWithInfo
                                    name="salary"
                                    label={<RequiredLabel text="Salary" />}
                                    value={formData.salary}
                                    onChange={handleChange}
                                    error={errors.salary}
                                    tooltipText="Provide the salary of the sponsor (e.g., in CAD or USD)."
                                />
                                <TextFieldWithInfo
                                    name="responsibilities"
                                    label={<RequiredLabel text="Responsibilities" />}
                                    value={formData.responsibilities}
                                    onChange={handleChange}
                                    error={errors.responsibilities}
                                    tooltipText="Provide a brief description of the sponsor's responsibilities in their job (e.g., managing a team, overseeing projects, etc.)."
                                />
                                <DateFieldWithInfo
                                    name="leaveStart"
                                    label={<RequiredLabel text="Leave Start Date" />}
                                    value={formData.leaveStart}
                                    onChange={handleChange}
                                    error={errors.leaveStart}
                                    tooltipText="Provide the date you plan to start your leave from work for your travel to Canada."
                                />
                                <DateFieldWithInfo
                                    name="leaveEnd"
                                    label={<RequiredLabel text="Leave End Date" />}
                                    value={formData.leaveEnd}
                                    onChange={handleChange}
                                    error={errors.leaveEnd}
                                    tooltipText="Provide the date you plan to end your leave in Canada."
                                />
                                <TextFieldWithInfo
                                    name="returnConfirmation"
                                    label={<RequiredLabel text="Return Confirmation Date" />}
                                    value={formData.returnConfirmation}
                                    onChange={handleChange}
                                    error={errors.returnConfirmation}
                                    tooltipText="Provide the date you plan to return to your home country after your stay in Canada."
                                />
                                <TextFieldWithInfo
                                    name="signatoryName"
                                    label={<RequiredLabel text="Signatory Name" />}
                                    value={formData.signatoryName}
                                    onChange={handleChange}
                                    error={errors.signatoryName}
                                    tooltipText="Provide the name of the person who will be signing the reference letter (e.g., your supervisor, manager, or legal representative)."
                                />
                                <TextFieldWithInfo
                                    name="signatoryPosition"
                                    label={<RequiredLabel text="Signatory Position" />}
                                    value={formData.signatoryPosition}
                                    onChange={handleChange}
                                    error={errors.signatoryPosition}
                                    tooltipText="Provide the position of the person who will be signing the reference letter (e.g., Manager, Supervisor, Legal Representative)."
                                />
                                <TextFieldWithInfo
                                    name="contactEmail"
                                    label={<RequiredLabel text="Signatory Contact Email" />}
                                    value={formData.contactEmail}
                                    onChange={handleChange}
                                    error={errors.contactEmail}
                                    tooltipText="Provide the contact email of the person who will be signing the reference letter (e.g., your supervisor, manager, or legal representative)."
                                />
                                <TextFieldWithInfo
                                    name="contactPhone"
                                    label={<RequiredLabel text="Signatory Contact Phone" />}
                                    value={formData.contactPhone}
                                    onChange={handleChange}
                                    error={errors.contactPhone}
                                    tooltipText="Provide the contact phone number of the person who will be signing the reference letter (e.g., your supervisor, manager, or legal representative)."
                                />
                            </div>
                        </>
                    )}

                {step === 1 && formData.sopType === SOPTypes.REFERENCE_LETTER_FOR_STUDENT_VISA && (
                    <>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '8px 16px',
                                marginTop: '16px',
                            }}
                        >
                            <SelectField
                                name="sopType"
                                label={<RequiredLabel text="Select SOP Type" />}
                                value={formData.sopType}
                                onChange={handleChange}
                                error={errors.sopType}
                                tooltipText="Choose the purpose of your SOP. For example, select “Student Visa” if you are applying for a Canadian study permit."
                            />

                            <p>
                                <b>Note:</b> Ensure the visa eligibility questionnaire details are
                                accurate, as they will be used by the SOP writer to draft your
                                letter.
                            </p>

                            <TextFieldWithInfo
                                name="studyProgram"
                                label={<RequiredLabel text="Select Program Name" />}
                                value={formData.studyProgram}
                                onChange={handleChange}
                                error={errors.studyProgram}
                                tooltipText="Provide the name of the academic program you are enrolled in (e.g., MSc in Computer Science, Diploma in Business Management)."
                            />

                            <TextFieldWithInfo
                                name="institutionName"
                                label={<RequiredLabel text="Select Institution Name" />}
                                value={formData.institutionName}
                                onChange={handleChange}
                                error={errors.institutionName}
                                tooltipText="Enter the full official name of the Canadian institution where you have been accepted (e.g., University of Toronto, Seneca College)."
                            />

                            <TextFieldWithInfo
                                name="institutionCountry"
                                label={<RequiredLabel text="Country of Institution" />}
                                value={formData.institutionCountry}
                                onChange={handleChange}
                                error={errors.institutionCountry}
                                tooltipText="Specify the country where you will pursue your studies."
                            />

                            <TextFieldWithInfo
                                name="refereeName"
                                label={<RequiredLabel text="Referee Name" />}
                                value={formData.refereeName}
                                onChange={handleChange}
                                error={errors.refereeName}
                                tooltipText="Provide the full name of the person writing the reference letter."
                            />

                            <TextFieldWithInfo
                                name="refereeOccupation"
                                label={<RequiredLabel text="Referee Occupation" />}
                                value={formData.refereeOccupation}
                                onChange={handleChange}
                                error={errors.refereeOccupation}
                                tooltipText="Provide the occupation or designation of the referee."
                            />

                            <TextFieldWithInfo
                                name="refereeOrganization"
                                label={<RequiredLabel text="Referee Organization" />}
                                value={formData.refereeOrganization}
                                onChange={handleChange}
                                error={errors.refereeOrganization}
                                tooltipText="Provide the name of the organization or institution where the referee works."
                            />

                            <TextFieldWithInfo
                                name="refereeAddress"
                                label={<RequiredLabel text="Referee Address" />}
                                value={formData.refereeAddress}
                                onChange={handleChange}
                                error={errors.refereeAddress}
                                tooltipText="Provide the official address of the referee's organization."
                            />

                            <TextFieldWithInfo
                                name="relationship"
                                label={<RequiredLabel text="Relationship with Applicant" />}
                                value={formData.relationship}
                                onChange={handleChange}
                                error={errors.relationship}
                                tooltipText="Describe your relationship with the referee (e.g., Professor, Manager, Supervisor)."
                            />

                            <TextFieldWithInfo
                                name="yearsKnown"
                                label={<RequiredLabel text="Years Known" />}
                                value={formData.yearsKnown}
                                onChange={handleChange}
                                error={errors.yearsKnown}
                                tooltipText="Specify how many years the referee has known you."
                            />

                            <TextFieldWithInfo
                                name="refereeContact"
                                label={<RequiredLabel text="Referee Contact Details" />}
                                value={formData.refereeContact}
                                onChange={handleChange}
                                error={errors.refereeContact}
                                tooltipText="Provide the contact details of the referee (email and/or phone number)."
                            />

                            <TextFieldWithInfo
                                name="homeCountryTies"
                                label={<RequiredLabel text="Home Country Ties" />}
                                value={formData.homeCountryTies}
                                onChange={handleChange}
                                error={errors.homeCountryTies}
                                tooltipText="Describe your strong ties to your home country (e.g., family, property, employment commitments)."
                            />

                            <TextFieldWithInfo
                                name="returnIntent"
                                label={<RequiredLabel text="Intent to Return" />}
                                value={formData.returnIntent}
                                onChange={handleChange}
                                error={errors.returnIntent}
                                tooltipText="Clearly explain your intention to return to your home country after completing your studies."
                            />
                        </div>
                    </>
                )}

                {step === 1 &&
                    formData.sopType === SOPTypes.STUDENT_VISA_AFFIDAVIT_FINANCIAL_SUPPORT && (
                        <>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '8px 16px',
                                    marginTop: '16px',
                                }}
                            >
                                <SelectField
                                    name="sopType"
                                    label={<RequiredLabel text="Select SOP Type" />}
                                    value={formData.sopType}
                                    onChange={handleChange}
                                    error={errors.sopType}
                                    tooltipText="Choose the purpose of your SOP. For example, select “Student Visa” if you are applying for a Canadian study permit."
                                />

                                <p>
                                    <b>Note:</b> Ensure the visa eligibility questionnaire details
                                    are accurate, as they will be used by the SOP writer to draft
                                    your letter.
                                </p>

                                <TextFieldWithInfo
                                    name="studentPassport"
                                    label={<RequiredLabel text="Student Passport Number" />}
                                    value={formData.studentPassport}
                                    onChange={handleChange}
                                    error={errors.studentPassport}
                                    tooltipText="Provide the passport number of the student."
                                />

                                <TextFieldWithInfo
                                    name="program"
                                    label={<RequiredLabel text="Program Name" />}
                                    value={formData.program}
                                    onChange={handleChange}
                                    error={errors.program}
                                    tooltipText="Provide the name of the academic program the student has applied for."
                                />

                                <TextFieldWithInfo
                                    name="institutionName"
                                    label={<RequiredLabel text="Select Institution Name" />}
                                    value={formData.institutionName}
                                    onChange={handleChange}
                                    error={errors.institutionName}
                                    tooltipText="Enter the full official name of the Canadian institution where you have been accepted (e.g., University of Toronto, Seneca College)."
                                />

                                <TextFieldWithInfo
                                    name="institutionCountry"
                                    label={<RequiredLabel text="Country of Institution" />}
                                    value={formData.institutionCountry}
                                    onChange={handleChange}
                                    error={errors.institutionCountry}
                                    tooltipText="Specify the country where you will pursue your studies."
                                />

                                <TextFieldWithInfo
                                    name="sponsorName"
                                    label={<RequiredLabel text="Sponsor Name" />}
                                    value={formData.sponsorName}
                                    onChange={handleChange}
                                    error={errors.sponsorName}
                                />

                                <TextFieldWithInfo
                                    name="relationship"
                                    label={<RequiredLabel text="Relationship to Student" />}
                                    value={formData.relationship}
                                    onChange={handleChange}
                                    error={errors.relationship}
                                    tooltipText="Specify your relationship to the student (e.g., parent, guardian, relative)."
                                />

                                <TextFieldWithInfo
                                    name="sponsorOccupation"
                                    label={<RequiredLabel text="Sponsor Occupation" />}
                                    value={formData.sponsorOccupation}
                                    onChange={handleChange}
                                    error={errors.sponsorOccupation}
                                    tooltipText="Provide the occupation of the sponsor."
                                />

                                <TextFieldWithInfo
                                    name="sponsorEmployer"
                                    label={<RequiredLabel text="Sponsor Employer" />}
                                    value={formData.sponsorEmployer}
                                    onChange={handleChange}
                                    error={errors.sponsorEmployer}
                                    tooltipText="Provide the name of the sponsor’s employer or business."
                                />

                                <TextFieldWithInfo
                                    name="sponsorAddress"
                                    label={<RequiredLabel text="Sponsor Address" />}
                                    value={formData.sponsorAddress}
                                    onChange={handleChange}
                                    error={errors.sponsorAddress}
                                    tooltipText="Provide the full residential address of the sponsor."
                                />

                                <TextFieldWithInfo
                                    name="sponsorContact"
                                    label={<RequiredLabel text="Sponsor Contact Number" />}
                                    value={formData.sponsorContact}
                                    onChange={handleChange}
                                    error={errors.sponsorContact}
                                    tooltipText="Provide the contact phone number of the sponsor."
                                />

                                <TextFieldWithInfo
                                    name="sponsorEmail"
                                    label={<RequiredLabel text="Sponsor Email" />}
                                    value={formData.sponsorEmail}
                                    onChange={handleChange}
                                    error={errors.sponsorEmail}
                                    tooltipText="Provide the email address of the sponsor."
                                />

                                <TextFieldWithInfo
                                    name="financialCommitment"
                                    label={<RequiredLabel text="Financial Commitment Details" />}
                                    value={formData.financialCommitment}
                                    onChange={handleChange}
                                    error={errors.financialCommitment}
                                    tooltipText="Clearly state the financial support being provided (e.g., tuition fees, living expenses, travel costs)."
                                />

                                <TextFieldWithInfo
                                    name="sourceOfFunds"
                                    label={<RequiredLabel text="Source of Funds" />}
                                    value={formData.sourceOfFunds}
                                    onChange={handleChange}
                                    error={errors.sourceOfFunds}
                                    tooltipText="Specify the source of funds (e.g., savings, business income, property income, fixed deposits)."
                                />

                                <TextFieldWithInfo
                                    name="returnIntent"
                                    label={<RequiredLabel text="Student's Intent to Return" />}
                                    value={formData.returnIntent}
                                    onChange={handleChange}
                                    error={errors.returnIntent}
                                    tooltipText="Explain the student’s intention to return to their home country after completing their studies."
                                />
                            </div>
                        </>
                    )}

                {step === 1 && formData.sopType === SOPTypes.SUBMISSION_LETTER_STUDENT_VISA && (
                    <>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '8px 16px',
                                marginTop: '16px',
                            }}
                        >
                            <SelectField
                                name="sopType"
                                label={<RequiredLabel text="Select SOP Type" />}
                                value={formData.sopType}
                                onChange={handleChange}
                                error={errors.sopType}
                                tooltipText="Choose the purpose of your SOP. For example, select “Student Visa” if you are applying for a Canadian study permit."
                            />

                            <p>
                                <b>Note:</b> Ensure the visa eligibility questionnaire details are
                                accurate, as they will be used by the SOP writer to draft your
                                letter.
                            </p>

                            <DateFieldWithInfo
                                name="dob"
                                label={<RequiredLabel text="Date of Birth" />}
                                value={formData.dob}
                                onChange={handleChange}
                                error={errors.dob}
                                tooltipText="Provide your date of birth as mentioned on your passport."
                            />

                            <TextFieldWithInfo
                                name="passport"
                                label={<RequiredLabel text="Passport Number" />}
                                value={formData.passport}
                                onChange={handleChange}
                                error={errors.passport}
                                tooltipText="Provide your valid passport number."
                            />

                            <TextFieldWithInfo
                                name="program"
                                label={<RequiredLabel text="Program Name" />}
                                value={formData.program}
                                onChange={handleChange}
                                error={errors.program}
                                tooltipText="Provide the name of the academic program you have been admitted to."
                            />

                            <TextFieldWithInfo
                                name="institutionName"
                                label={<RequiredLabel text="Select Institution Name" />}
                                value={formData.institutionName}
                                onChange={handleChange}
                                error={errors.institutionName}
                                tooltipText="Enter the full official name of the Canadian institution where you have been accepted (e.g., University of Toronto, Seneca College)."
                            />

                            <DateFieldWithInfo
                                name="startDate"
                                label={<RequiredLabel text="Program Start Date" />}
                                value={formData.startDate}
                                onChange={handleChange}
                                error={errors.startDate}
                                tooltipText="Provide the official start date of your program."
                            />

                            <TextFieldWithInfo
                                name="duration"
                                label={<RequiredLabel text="Program Duration" />}
                                value={formData.duration}
                                onChange={handleChange}
                                error={errors.duration}
                                tooltipText="Specify the total duration of your program (e.g., 2 years)."
                            />

                            <TextFieldWithInfo
                                name="tuitionPaid"
                                label={<RequiredLabel text="Tuition Fees Paid" />}
                                value={formData.tuitionPaid}
                                onChange={handleChange}
                                error={errors.tuitionPaid}
                                tooltipText="Mention how much tuition fee has already been paid, if applicable."
                            />

                            <TextFieldWithInfo
                                name="availableFunds"
                                label={<RequiredLabel text="Available Funds" />}
                                value={formData.availableFunds}
                                onChange={handleChange}
                                error={errors.availableFunds}
                                tooltipText="Specify the total funds available for tuition and living expenses."
                            />

                            <TextFieldWithInfo
                                name="sourceOfFunds"
                                label={<RequiredLabel text="Source of Funds" />}
                                value={formData.sourceOfFunds}
                                onChange={handleChange}
                                error={errors.sourceOfFunds}
                                tooltipText="Explain the source of your funds (e.g., savings, family support, loan, scholarship)."
                            />

                            <TextFieldWithInfo
                                name="previousRefusal"
                                label={<RequiredLabel text="Previous Visa Refusal (If Any)" />}
                                value={formData.previousRefusal}
                                onChange={handleChange}
                                error={errors.previousRefusal}
                                tooltipText="Indicate whether you have had any previous visa refusals."
                            />

                            <TextFieldWithInfo
                                name="refusalExplanation"
                                label={<RequiredLabel text="Refusal Explanation" />}
                                value={formData.refusalExplanation}
                                onChange={handleChange}
                                error={errors.refusalExplanation}
                                tooltipText="If applicable, explain the reason for previous refusal and how you have addressed it."
                            />

                            <TextFieldWithInfo
                                name="homeCountryTies"
                                label={<RequiredLabel text="Home Country Ties" />}
                                value={formData.homeCountryTies}
                                onChange={handleChange}
                                error={errors.homeCountryTies}
                                tooltipText="Describe your strong ties to your home country (family, employment, property, business)."
                            />

                            <TextFieldWithInfo
                                name="assets"
                                label={<RequiredLabel text="Assets" />}
                                value={formData.assets}
                                onChange={handleChange}
                                error={errors.assets}
                                tooltipText="List any significant assets owned by you or your family."
                            />

                            <TextFieldWithInfo
                                name="travelHistory"
                                label={<RequiredLabel text="Travel History" />}
                                value={formData.travelHistory}
                                onChange={handleChange}
                                error={errors.travelHistory}
                                tooltipText="Provide details of your previous international travel history, if any."
                            />

                            <TextFieldWithInfo
                                name="documentsIncluded"
                                label={<RequiredLabel text="Documents Included" />}
                                value={formData.documentsIncluded}
                                onChange={handleChange}
                                error={errors.documentsIncluded}
                                tooltipText="List the key documents included in your visa application."
                            />

                            <TextFieldWithInfo
                                name="representativeName"
                                label={<RequiredLabel text="Representative Name (If Any)" />}
                                value={formData.representativeName}
                                onChange={handleChange}
                                error={errors.representativeName}
                                tooltipText="Provide the name of your authorized immigration representative, if applicable."
                            />

                            <TextFieldWithInfo
                                name="representativeRCIC"
                                label={<RequiredLabel text="Representative RCIC Number" />}
                                value={formData.representativeRCIC}
                                onChange={handleChange}
                                error={errors.representativeRCIC}
                                tooltipText="Provide the RCIC number of your immigration representative, if applicable."
                            />
                        </div>
                    </>
                )}

                {step === 1 && formData.sopType === SOPTypes.SUBMISSION_LETTER_TOURIST_VISA && (
                    <>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '8px 16px',
                                marginTop: '16px',
                            }}
                        >
                            <SelectField
                                name="sopType"
                                label={<RequiredLabel text="Select SOP Type" />}
                                value={formData.sopType}
                                onChange={handleChange}
                                error={errors.sopType}
                                tooltipText="Choose the purpose of your SOP. For example, select “Student Visa” if you are applying for a Canadian study permit."
                            />

                            <p>
                                <b>Note:</b> Ensure the visa eligibility questionnaire details are
                                accurate, as they will be used by the SOP writer to draft your
                                letter.
                            </p>

                            <DateFieldWithInfo
                                name="dob"
                                label={<RequiredLabel text="Date of Birth" />}
                                value={formData.dob}
                                onChange={handleChange}
                                error={errors.dob}
                                tooltipText="Provide your date of birth as mentioned on your passport."
                            />

                            <TextFieldWithInfo
                                name="passport"
                                label={<RequiredLabel text="Passport Number" />}
                                value={formData.passport}
                                onChange={handleChange}
                                error={errors.passport}
                                tooltipText="Provide your valid passport number."
                            />

                            <TextFieldWithInfo
                                name="additionalApplicants"
                                label={<RequiredLabel text="Additional Applicants (If Any)" />}
                                value={formData.additionalApplicants}
                                onChange={handleChange}
                                error={errors.additionalApplicants}
                                tooltipText="Mention if any family members are applying with you."
                            />

                            <DateFieldWithInfo
                                name="travelStart"
                                label={<RequiredLabel text="Travel Start Date" />}
                                value={formData.travelStart}
                                onChange={handleChange}
                                error={errors.travelStart}
                                tooltipText="Provide the planned start date of your travel."
                            />

                            <DateFieldWithInfo
                                name="travelEnd"
                                label={<RequiredLabel text="Travel End Date" />}
                                value={formData.travelEnd}
                                onChange={handleChange}
                                error={errors.travelEnd}
                                tooltipText="Provide the planned end date of your travel."
                            />

                            <TextFieldWithInfo
                                name="purpose"
                                label={<RequiredLabel text="Purpose of Visit" />}
                                value={formData.purpose}
                                onChange={handleChange}
                                error={errors.purpose}
                                tooltipText="Clearly describe the purpose of your visit (e.g., tourism, visiting family, attending an event)."
                            />

                            <TextFieldWithInfo
                                name="accommodation"
                                label={<RequiredLabel text="Accommodation Details" />}
                                value={formData.accommodation}
                                onChange={handleChange}
                                error={errors.accommodation}
                                tooltipText="Provide details of where you will stay during your visit (hotel, relative's home, Airbnb, etc.)."
                            />

                            <TextFieldWithInfo
                                name="employment"
                                label={<RequiredLabel text="Current Employment Details" />}
                                value={formData.employment}
                                onChange={handleChange}
                                error={errors.employment}
                                tooltipText="Provide details of your current employment or business."
                            />

                            <TextFieldWithInfo
                                name="residenceCountry"
                                label={<RequiredLabel text="Country of Residence" />}
                                value={formData.residenceCountry}
                                onChange={handleChange}
                                error={errors.residenceCountry}
                                tooltipText="Specify the country where you currently reside."
                            />

                            <TextFieldWithInfo
                                name="financialSummary"
                                label={<RequiredLabel text="Financial Summary" />}
                                value={formData.financialSummary}
                                onChange={handleChange}
                                error={errors.financialSummary}
                                tooltipText="Summarize your financial capacity to support your trip (savings, income, assets)."
                            />

                            <TextFieldWithInfo
                                name="homeCountryTies"
                                label={<RequiredLabel text="Home Country Ties" />}
                                value={formData.homeCountryTies}
                                onChange={handleChange}
                                error={errors.homeCountryTies}
                                tooltipText="Describe strong ties to your home country (family, job, property, business)."
                            />

                            <TextFieldWithInfo
                                name="documentsIncluded"
                                label={<RequiredLabel text="Documents Included" />}
                                value={formData.documentsIncluded}
                                onChange={handleChange}
                                error={errors.documentsIncluded}
                                tooltipText="List the documents included with your visa application."
                            />

                            <TextFieldWithInfo
                                name="representativeName"
                                label={<RequiredLabel text="Representative Name (If Any)" />}
                                value={formData.representativeName}
                                onChange={handleChange}
                                error={errors.representativeName}
                                tooltipText="Provide the name of your authorized immigration representative, if applicable."
                            />

                            <TextFieldWithInfo
                                name="representativeRCIC"
                                label={<RequiredLabel text="Representative RCIC Number" />}
                                value={formData.representativeRCIC}
                                onChange={handleChange}
                                error={errors.representativeRCIC}
                                tooltipText="Provide the RCIC number of your immigration representative, if applicable."
                            />

                            <TextFieldWithInfo
                                name="visaOffice"
                                label={<RequiredLabel text="Visa Office" />}
                                value={formData.visaOffice}
                                onChange={handleChange}
                                error={errors.visaOffice}
                                tooltipText="Specify the visa office where your application will be processed."
                            />
                        </div>
                    </>
                )}

                {step === 1 && formData.sopType === SOPTypes.TOURIST_VISA_INVITATION_LETTER && (
                    <>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '8px 16px',
                                marginTop: '16px',
                            }}
                        >
                            <SelectField
                                name="sopType"
                                label={<RequiredLabel text="Select SOP Type" />}
                                value={formData.sopType}
                                onChange={handleChange}
                                error={errors.sopType}
                                tooltipText="Choose the purpose of your SOP."
                            />

                            <p>
                                <b>Note:</b> Ensure the details provided are accurate, as they will
                                be used to draft the official invitation letter.
                            </p>

                            <TextFieldWithInfo
                                name="statusInCanada"
                                label={<RequiredLabel text="Inviter Status in Canada" />}
                                value={formData.statusInCanada}
                                onChange={handleChange}
                                error={errors.statusInCanada}
                                tooltipText="Specify your status in Canada (e.g., Citizen, Permanent Resident, Work Permit Holder, Student)."
                            />

                            <TextFieldWithInfo
                                name="inviterOccupation"
                                label={<RequiredLabel text="Inviter Occupation" />}
                                value={formData.inviterOccupation}
                                onChange={handleChange}
                                error={errors.inviterOccupation}
                                tooltipText="Provide your current occupation."
                            />

                            <TextFieldWithInfo
                                name="inviterEmployer"
                                label={<RequiredLabel text="Inviter Employer Name" />}
                                value={formData.inviterEmployer}
                                onChange={handleChange}
                                error={errors.inviterEmployer}
                                tooltipText="Provide the name of your employer or business."
                            />

                            <TextFieldWithInfo
                                name="inviterAddress"
                                label={<RequiredLabel text="Inviter Residential Address" />}
                                value={formData.inviterAddress}
                                onChange={handleChange}
                                error={errors.inviterAddress}
                                tooltipText="Provide your full residential address in Canada."
                            />

                            <TextFieldWithInfo
                                name="inviterPhone"
                                label={<RequiredLabel text="Inviter Phone Number" />}
                                value={formData.inviterPhone}
                                onChange={handleChange}
                                error={errors.inviterPhone}
                                tooltipText="Provide your contact phone number."
                            />

                            <TextFieldWithInfo
                                name="inviterEmail"
                                label={<RequiredLabel text="Inviter Email Address" />}
                                value={formData.inviterEmail}
                                onChange={handleChange}
                                error={errors.inviterEmail}
                                tooltipText="Provide your active email address."
                            />

                            <TextFieldWithInfo
                                name="visitorName"
                                label={<RequiredLabel text="Visitor Full Name" />}
                                value={formData.visitorName}
                                onChange={handleChange}
                                error={errors.visitorName}
                                tooltipText="Provide the full legal name of the visitor as per passport."
                            />

                            <TextFieldWithInfo
                                name="visitorPassport"
                                label={<RequiredLabel text="Visitor Passport Number" />}
                                value={formData.visitorPassport}
                                onChange={handleChange}
                                error={errors.visitorPassport}
                                tooltipText="Provide the visitor’s valid passport number."
                            />

                            <TextFieldWithInfo
                                name="visitorAddress"
                                label={<RequiredLabel text="Visitor Residential Address" />}
                                value={formData.visitorAddress}
                                onChange={handleChange}
                                error={errors.visitorAddress}
                                tooltipText="Provide the visitor’s full residential address in their home country."
                            />

                            <TextFieldWithInfo
                                name="relationship"
                                label={<RequiredLabel text="Relationship to Visitor" />}
                                value={formData.relationship}
                                onChange={handleChange}
                                error={errors.relationship}
                                tooltipText="Specify your relationship with the visitor (e.g., parent, sibling, friend)."
                            />

                            <DateFieldWithInfo
                                name="visitStart"
                                label={<RequiredLabel text="Visit Start Date" />}
                                value={formData.visitStart}
                                onChange={handleChange}
                                error={errors.visitStart}
                                tooltipText="Provide the planned start date of the visit."
                            />

                            <DateFieldWithInfo
                                name="visitEnd"
                                label={<RequiredLabel text="Visit End Date" />}
                                value={formData.visitEnd}
                                onChange={handleChange}
                                error={errors.visitEnd}
                                tooltipText="Provide the planned end date of the visit."
                            />

                            <TextFieldWithInfo
                                name="purpose"
                                label={<RequiredLabel text="Purpose of Visit" />}
                                value={formData.purpose}
                                onChange={handleChange}
                                error={errors.purpose}
                                tooltipText="Explain the purpose of the visit (tourism, family visit, event, etc.)."
                            />

                            <TextFieldWithInfo
                                name="itinerary"
                                label={<RequiredLabel text="Planned Itinerary" />}
                                value={formData.itinerary}
                                onChange={handleChange}
                                error={errors.itinerary}
                                tooltipText="Provide details of planned activities or places to visit during the stay."
                            />

                            <TextFieldWithInfo
                                name="accommodationDetails"
                                label={<RequiredLabel text="Accommodation Details" />}
                                value={formData.accommodationDetails}
                                onChange={handleChange}
                                error={errors.accommodationDetails}
                                tooltipText="Specify where the visitor will stay (your home, hotel, etc.)."
                            />

                            <TextFieldWithInfo
                                name="financialResponsibility"
                                label={<RequiredLabel text="Financial Responsibility" />}
                                value={formData.financialResponsibility}
                                onChange={handleChange}
                                error={errors.financialResponsibility}
                                tooltipText="Clarify who will bear the expenses of the trip (visitor, inviter, shared)."
                            />

                            <TextFieldWithInfo
                                name="supportingDocuments"
                                label={<RequiredLabel text="Supporting Documents" />}
                                value={formData.supportingDocuments}
                                onChange={handleChange}
                                error={errors.supportingDocuments}
                                tooltipText="List the documents you will provide (PR card, passport copy, employment letter, etc.)."
                            />

                            <TextFieldWithInfo
                                name="returnAssurance"
                                label={<RequiredLabel text="Return Assurance" />}
                                value={formData.returnAssurance}
                                onChange={handleChange}
                                error={errors.returnAssurance}
                                tooltipText="Confirm that the visitor will return to their home country after the visit and explain supporting reasons (job, family, property, etc.)."
                            />
                        </div>
                    </>
                )}

                {step === 1 && formData.sopType === SOPTypes.TV_AFFIDAVIT_FINANCIAL_PURPOSE && (
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '8px 16px',
                            marginTop: '16px',
                        }}
                    >
                        <SelectField
                            name="sopType"
                            label={<RequiredLabel text="Select SOP Type" />}
                            value={formData.sopType}
                            onChange={handleChange}
                            error={errors.sopType}
                            tooltipText="Select the type of SOP or affidavit you want to generate. This determines the required information for the document."
                        />

                        <TextFieldWithInfo
                            name="passport"
                            label={<RequiredLabel text="Applicant Passport Number" />}
                            value={formData.passport}
                            onChange={handleChange}
                            error={errors.passport}
                            tooltipText="Enter the passport number of the applicant exactly as shown on the passport."
                        />

                        <TextFieldWithInfo
                            name="sponsorName"
                            label={<RequiredLabel text="Sponsor Full Name" />}
                            value={formData.sponsorName}
                            onChange={handleChange}
                            error={errors.sponsorName}
                            tooltipText="Provide the full legal name of the person who will financially support the applicant during the visit."
                        />

                        <TextFieldWithInfo
                            name="relationship"
                            label={<RequiredLabel text="Relationship to Applicant" />}
                            value={formData.relationship}
                            onChange={handleChange}
                            error={errors.relationship}
                            tooltipText="Specify the relationship between the sponsor and the applicant (e.g., parent, sibling, uncle, family friend)."
                        />

                        <TextFieldWithInfo
                            name="sponsorAddress"
                            label={<RequiredLabel text="Sponsor Address" />}
                            value={formData.sponsorAddress}
                            onChange={handleChange}
                            error={errors.sponsorAddress}
                            tooltipText="Enter the full residential address of the sponsor including city, state, and country."
                        />

                        <TextFieldWithInfo
                            name="sponsorOccupation"
                            label={<RequiredLabel text="Sponsor Occupation" />}
                            value={formData.sponsorOccupation}
                            onChange={handleChange}
                            error={errors.sponsorOccupation}
                            tooltipText="Provide the sponsor’s current occupation or job title."
                        />

                        <TextFieldWithInfo
                            name="incomeSource"
                            label={<RequiredLabel text="Source of Income" />}
                            value={formData.incomeSource}
                            onChange={handleChange}
                            error={errors.incomeSource}
                            tooltipText="Explain how the sponsor earns income (e.g., employment, business, investments, pension)."
                        />

                        <TextFieldWithInfo
                            name="purpose"
                            label={<RequiredLabel text="Purpose of Visit" />}
                            value={formData.purpose}
                            onChange={handleChange}
                            error={errors.purpose}
                            tooltipText="Clearly describe the reason for the visit (tourism, attending an event, visiting family, etc.)."
                        />

                        <TextFieldWithInfo
                            name="eventDetails"
                            label={<RequiredLabel text="Event / Visit Details" />}
                            value={formData.eventDetails}
                            onChange={handleChange}
                            error={errors.eventDetails}
                            tooltipText="Provide additional details about the visit or event, including any planned activities or specific occasions."
                        />

                        <DateFieldWithInfo
                            name="travelStart"
                            label={<RequiredLabel text="Travel Start Date" />}
                            value={formData.travelStart}
                            onChange={handleChange}
                            error={errors.travelStart}
                            tooltipText="Select the planned date when the applicant intends to begin travel."
                        />

                        <DateFieldWithInfo
                            name="travelEnd"
                            label={<RequiredLabel text="Travel End Date" />}
                            value={formData.travelEnd}
                            onChange={handleChange}
                            error={errors.travelEnd}
                            tooltipText="Select the planned date when the applicant intends to return to their home country."
                        />

                        <TextFieldWithInfo
                            name="financialSupport"
                            label={<RequiredLabel text="Financial Support Details" />}
                            value={formData.financialSupport}
                            onChange={handleChange}
                            error={errors.financialSupport}
                            tooltipText="Describe how the sponsor will financially support the applicant during the visit (travel costs, accommodation, daily expenses)."
                        />

                        <TextFieldWithInfo
                            name="funds"
                            label={<RequiredLabel text="Available Funds" />}
                            value={formData.funds}
                            onChange={handleChange}
                            error={errors.funds}
                            tooltipText="Specify the approximate funds available to support the visit (for example savings or bank balance)."
                        />

                        <TextFieldWithInfo
                            name="bankProof"
                            label={<RequiredLabel text="Bank Proof Provided" />}
                            value={formData.bankProof}
                            onChange={handleChange}
                            error={errors.bankProof}
                            tooltipText="Mention the financial proof that will be provided such as bank statements, account balance certificates, or financial documents."
                        />

                        <TextFieldWithInfo
                            name="relationshipEvidence"
                            label={<RequiredLabel text="Relationship Evidence" />}
                            value={formData.relationshipEvidence}
                            onChange={handleChange}
                            error={errors.relationshipEvidence}
                            tooltipText="Specify documents that prove the relationship between the sponsor and the applicant (birth certificates, family records, etc.)."
                        />

                        <TextFieldWithInfo
                            name="documentsIncluded"
                            label={<RequiredLabel text="Documents Included" />}
                            value={formData.documentsIncluded}
                            onChange={handleChange}
                            error={errors.documentsIncluded}
                            tooltipText="List all supporting documents that will accompany the affidavit (passport copies, bank statements, ID proof, relationship proof)."
                        />
                    </div>
                )}

                {step === 2 && (
                    <div style={{ width: '100%', marginTop: '16px' }}>
                        {loading ? (
                            <S.LoadingContainer>
                                <Typography
                                    variant="h6"
                                    sx={{ color: '#8353e2', fontWeight: 600, mb: 1 }}
                                >
                                    Generating your SOP...
                                </Typography>
                                <LinearProgress
                                    sx={{
                                        width: '100%',
                                        maxWidth: 360,
                                        borderRadius: 4,
                                        mb: 2,
                                        '& .MuiLinearProgress-bar': { background: '#8353e2' },
                                    }}
                                />
                                {LOADING_STEPS.map((s, i) => (
                                    <S.LoadingStep
                                        key={i}
                                        active={loadingStep === i + 1}
                                        done={loadingStep > i + 1 || (!loading && !sopError)}
                                    >
                                        <S.StepIcon
                                            done={loadingStep > i + 1 || (!loading && !sopError)}
                                        >
                                            {loadingStep > i + 1 || (!loading && !sopError) ? (
                                                <CheckIcon sx={{ fontSize: 14 }} />
                                            ) : loadingStep === i + 1 ? (
                                                <CircularProgress
                                                    size={12}
                                                    sx={{ color: 'white' }}
                                                />
                                            ) : (
                                                i + 1
                                            )}
                                        </S.StepIcon>
                                        <Typography variant="body2" sx={{ color: '#333' }}>
                                            {s.label}
                                        </Typography>
                                    </S.LoadingStep>
                                ))}
                                <Typography
                                    variant="caption"
                                    sx={{ color: '#888', mt: 1, textAlign: 'center' }}
                                >
                                    This may take up to a minute. Please don't close this window.
                                </Typography>
                            </S.LoadingContainer>
                        ) : sopError ? (
                            <S.ErrorContainer>
                                <ErrorOutlineIcon sx={{ fontSize: 48, color: '#e53935' }} />
                                <Typography variant="h6" sx={{ color: '#e53935', fontWeight: 600 }}>
                                    Generation Failed
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#555', maxWidth: 320 }}>
                                    {sopError}
                                </Typography>
                                <Button
                                    startIcon={<RefreshIcon />}
                                    variant="outlined"
                                    sx={{ borderColor: '#8353e2', color: '#8353e2', mt: 1 }}
                                    onClick={() => setRetryCount((c) => c + 1)}
                                >
                                    Try Again
                                </Button>
                            </S.ErrorContainer>
                        ) : (
                            <ReactQuill
                                ref={quillRef}
                                theme="snow"
                                value={generatedSOP}
                                onChange={setGeneratedSOP}
                            />
                        )}
                    </div>
                )}
            </S.ModalContent>
            <Actions>
                {step === 2 && (
                    <SecondaryButton
                        type="button"
                        onClick={() => {
                            setStep(1);
                            setSOPError(null);
                            setGeneratedSOP('');
                            setLoading(true);
                        }}
                    >
                        Back
                    </SecondaryButton>
                )}{' '}
                {step === 1 ? (
                    <PrimaryButton
                        type="button"
                        onClick={() => setStep(2)}
                        disabled={!isStep1Valid}
                    >
                        Next
                    </PrimaryButton>
                ) : (
                    <PrimaryButton
                        type="button"
                        onClick={() => generateSOPDoc()}
                        disabled={loading || !!sopError || !generatedSOP}
                    >
                        Download
                    </PrimaryButton>
                )}
            </Actions>
        </Modal>
    );
}

function SelectField({ name, label, value, onChange, error, tooltipText }: any) {
    return (
        <div style={{ minHeight: '58px' }}>
            <LabelWithInfo label={label} tooltip={tooltipText} />
            <Select name={name} value={value} onChange={onChange} fullWidth error={error}>
                {sopTypesOptions.map((option) => {
                    return (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    );
                })}
            </Select>
            <Typography
                color="error"
                sx={{ visibility: error ? 'visible' : 'hidden', minHeight: '14px', fontSize: 12 }}
            >
                This field is required
            </Typography>
        </div>
    );
}

function TextFieldWithInfo({ name, label, value, onChange, error, tooltipText }: any) {
    return (
        <div style={{ minHeight: '58px' }}>
            <LabelWithInfo label={label} tooltip={tooltipText} />
            <TextField name={name} value={value} onChange={onChange} fullWidth error={error} />
            <Typography
                color="error"
                sx={{ visibility: error ? 'visible' : 'hidden', minHeight: '14px', fontSize: 12 }}
            >
                This field is required
            </Typography>
        </div>
    );
}

function DateFieldWithInfo({ name, label, value, onChange, error, tooltipText }: any) {
    return (
        <div style={{ minHeight: '58px' }}>
            <LabelWithInfo label={label} tooltip={tooltipText} />
            <TextField
                name={name}
                type="date"
                value={value}
                onChange={onChange}
                fullWidth
                error={error}
                InputLabelProps={{
                    shrink: true,
                }}
            />
            <Typography
                color="error"
                sx={{ visibility: error ? 'visible' : 'hidden', minHeight: '14px', fontSize: 12 }}
            >
                This field is required
            </Typography>
        </div>
    );
}

// Reusable component for labels with info icons
function LabelWithInfo({ label, tooltip }: { label: string; tooltip: string }) {
    return (
        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', ml: 0 }}>
            {label}
            <Tooltip title={tooltip}>
                <IconButton size="small" sx={{ ml: 0.5 }}>
                    <InfoIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Typography>
    );
}

function RequiredLabel({ text }: { text: string }) {
    return (
        <Typography component="span">
            {text}
            <Typography component="span" sx={{ color: 'red', ml: 0.5 }}>
                *
            </Typography>
        </Typography>
    );
}
